import React, { useState } from "react";
import { Sidebar, Header } from "./components/Shell";
import { ShareBanner } from "./components/ShareBanner";
import { HomeScreen } from "./screens/Home";
import { CsvImportScreen } from "./screens/CsvImport";
import { StatementListScreen } from "./screens/StatementList";
import { SavedMonthsScreen } from "./screens/SavedMonths";
import { StatementDetailScreen } from "./screens/StatementDetail";
import { MasterEditScreen, type MasterEditInitial } from "./screens/MasterEdit";
import { EntryFormScreen } from "./screens/EntryForm";
import { SettingsScreen } from "./screens/Settings";
import { ApprovalStatusScreen } from "./screens/ApprovalStatus";
import { DriverApprovalScreen } from "./screens/DriverApproval";
import { RegisterStatusScreen } from "./screens/RegisterStatus";
import { AccountAdminScreen } from "./screens/AccountAdmin";
import { useAuth } from "./auth";
import { useStore } from "./store";
import { EMPTY_FILTERS, type StatementFilters } from "./lib/statementFilter";

export function App() {
  const [screen, setScreen] = useState("home");
  // R1: 人単位1通なので detail は driverId だけで一意
  const [detail, setDetail] = useState<{ driverId: string } | null>(null);
  // 一覧の絞り込み条件。個人明細へ移動しても保持し、前後ナビにも同じ条件を適用する（本城さん要望 2026-08-24）
  const [listFilters, setListFilters] = useState<StatementFilters>(EMPTY_FILTERS);
  const [masterInitial, setMasterInitial] = useState<MasterEditInitial | undefined>();
  // 携帯幅のときだけ使うメニューの開閉。PC幅ではサイドバーが常時出ているので使われない
  const [menuOpen, setMenuOpen] = useState(false);
  const { state, hasUnsavedWork, share } = useStore();
  const { member, authEnabled, logout } = useAuth();

  // ヘッダーの保存状況ピル。共有が有効な環境では「誰がいつ更新したか」まで出す
  // （本城さんの作業を天羽さんが見ているとき、いつ時点の内容かが分かるように）。
  const saveBadge = (() => {
    if (state.statements.length === 0 && share.mode === "init") return undefined;
    const rel = (iso: string | null) => {
      if (!iso) return "";
      const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
      if (!Number.isFinite(min)) return "";
      if (min < 1) return "たった今";
      if (min < 60) return `${min}分前`;
      const h = Math.floor(min / 60);
      return h < 24 ? `${h}時間前` : new Date(iso).toLocaleDateString("ja-JP");
    };
    switch (share.mode) {
      case "off":
        return {
          label: "この端末に保存",
          tone: "amber" as const,
          title: "共有機能が使えない環境のため、この端末のブラウザにだけ保存されます。",
        };
      case "saving":
        return { label: "共有に保存中...", tone: "amber" as const, title: "作業中の内容を全員の画面に反映しています。" };
      case "error":
        return {
          label: "共有していません",
          tone: "amber" as const,
          // 競合のときだけ画面上部に帯が出る。通信エラー等は帯が出ないので「探させない」文言にする
          title:
            (share.error ? share.error + "。" : "") +
            "この端末には内容が残っています。次に何か操作すると自動で保存し直します。",
        };
      case "synced":
        return {
          label: share.savedBy ? `共有中 ・ ${share.savedBy} ${rel(share.savedAt)}` : "共有中",
          tone: "green" as const,
          title: "作業中の内容は全員に共有されています。他の方が更新すると自動で反映されます。",
        };
      default:
        return state.statements.length > 0
          ? { label: "自動保存中", tone: hasUnsavedWork ? ("amber" as const) : ("green" as const) }
          : undefined;
    }
  })();

  const onSelectDetail = (driverId: string) => {
    setDetail({ driverId });
    setScreen("detail");
    window.scrollTo({ top: 0 });
  };

  const onAddMasterFromUnmatched = (name: string) => {
    setMasterInitial({ prefillName: name });
    setScreen("master");
    window.scrollTo({ top: 0 });
  };

  const crumbs = (() => {
    switch (screen) {
      case "home": return ["HSD月次処理", "ホーム"];
      case "csv": return ["HSD月次処理", state.targetMonth, "CSV取込"];
      case "list": return ["HSD月次処理", state.targetMonth, "支払明細 一覧"];
      case "saved": return ["HSD月次処理", "保存済みデータ"];
      case "detail": {
        const s = state.statements.find((s) => s.driverId === detail?.driverId);
        return ["HSD月次処理", state.targetMonth, "支払明細 一覧", s?.driverName || ""];
      }
      case "master": return ["HSD月次処理", "マスター編集"];
      case "entry": return ["HSD月次処理", "入社・契約解除フォーム"];
      case "approval": return ["HSD月次処理", state.targetMonth, "承認状況"];
      case "approvalPreview": return ["HSD月次処理", "承認状況", "ドライバー承認 (画面B・プレビュー)"];
      case "register": return ["HSD月次処理", "メアド収集"];
      case "accounts": return ["HSD月次処理", "アカウント管理"];
      case "settings": return ["HSD月次処理", "設定"];
      default: return ["HSD月次処理"];
    }
  })();

  const navTo = (s: string) => {
    if (s !== "master") setMasterInitial(undefined);
    setScreen(s);
    window.scrollTo({ top: 0 });
  };

  let body: React.ReactNode;
  if (screen === "home") body = <HomeScreen onNav={navTo} />;
  else if (screen === "csv") body = <CsvImportScreen onNav={navTo} />;
  else if (screen === "list") body = <StatementListScreen onNav={navTo} onSelect={onSelectDetail} onAddMaster={onAddMasterFromUnmatched} filters={listFilters} onFilters={setListFilters} />;
  else if (screen === "detail" && detail) body = <StatementDetailScreen driverId={detail.driverId} onNav={navTo} onSelectDriver={onSelectDetail} filters={listFilters} />;
  else if (screen === "saved") body = <SavedMonthsScreen onNav={navTo} />;
  else if (screen === "master") body = <MasterEditScreen initial={masterInitial} />;
  else if (screen === "entry") body = <EntryFormScreen onNav={navTo} />;
  else if (screen === "approval") body = <ApprovalStatusScreen onNav={navTo} onSelect={onSelectDetail} />;
  else if (screen === "approvalPreview") body = <DriverApprovalScreen />;
  else if (screen === "register") body = <RegisterStatusScreen onNav={navTo} />;
  else if (screen === "accounts") body = <AccountAdminScreen onNav={navTo} />;
  else if (screen === "settings") body = <SettingsScreen />;
  else body = <div className="content">準備中の画面です。</div>;


  return (
    <div className="app">
      <Sidebar
        current={screen === "detail" ? "list" : screen === "approvalPreview" ? "approval" : screen}
        onNav={navTo}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
      />
      <div className="main">
        <Header
          crumbs={crumbs}
          period={(() => {
            if (!/^\d{4}-\d{2}$/.test(state.targetMonth)) return state.targetMonth;
            const [y, m] = state.targetMonth.split("-").map(Number);
            return `${y}年${m}月`;
          })()}
          badge={
            state.statements.length > 0
              ? { label: `明細 ${state.statements.length} 件`, tone: "green" }
              : { label: "CSV未取込", tone: "amber" }
          }
          autosave={saveBadge}
          user={member ? { name: member.name, org: member.org, role: member.role } : null}
          onLogout={authEnabled ? logout : undefined}
          onMenu={() => setMenuOpen(true)}
        />
        <ShareBanner />
        {body}
      </div>
    </div>
  );
}
